<template>
  <div class="tpick">
    <div class="tpick__bar">
      <app-month-stepper v-model="month" />
      <app-search v-model="searchText" class="tpick__search" placeholder="Fahrt, Linie oder Strecke" @input="onSearchInput" />
    </div>
    <div class="tpick__grid">
      <dx-data-grid
        :ref="gridRefName"
        :height="360"
        :columns="columns"
        :data-source="tripDataSource"
        :show-borders="false"
        :show-column-lines="false"
        :show-row-lines="true"
        :column-auto-width="false"
        :hover-state-enabled="!gridTouch"
        :scrolling="pickScrolling"
        :paging="pickPaging"
        :element-attr="gridAttr"
        :focused-row-enabled="true"
        :no-data-text="noDataText"
        @toolbar-preparing="onToolbarPreparing"
        @focused-row-changing="onFocusedRowChanging"
        @focused-row-changed="onFocusedRowChanged"
        @row-prepared="onRowPrepared"
        @content-ready="onContentReady"
      >
        <template #timeTemplate="cell">
          <span class="tpick__time">{{ cell.data.data.start || "–" }}</span>
        </template>
        <template #dayTemplate="cell">
          <span class="tpick__num">{{ dayText(cell.data.data) }}</span>
        </template>
        <template #routeTemplate="cell">
          <span class="tpick__route" :title="routeText(cell.data.data)">{{ routeText(cell.data.data) }}</span>
        </template>
        <template #tripTemplate="cell">
          <span class="tpick__trip">
            <span class="tpick__num">{{ cell.data.data.trip_id }}</span>
            <span v-if="isCurrent(cell.data.data)" class="tpick__current">aktuell</span>
          </span>
        </template>
        <template #statusTemplate="cell">
          <span class="tpick__status">
            <span
              class="tpick__dot"
              :class="cell.data.data.state == 'ACTIVE' ? 'tpick__dot--ok' : 'tpick__dot--off'"
              aria-hidden="true"
            ></span>
            {{ cell.data.data.state == "ACTIVE" ? "Gültig" : "Storniert" }}
          </span>
        </template>
        <template #seatsTemplate="cell">
          <div class="tpick__seats">
            <app-share-bar
              class="tpick__bar-seats"
              :parts="[{ value: Number(cell.data.data.capacity_used) || 0, color: seatsColor(cell.data.data) }]"
              :total="Number(cell.data.data.capacity) || 0"
            />
            <span class="tpick__num">{{ freeText(cell.data.data) }}</span>
          </div>
        </template>
      </dx-data-grid>
    </div>
    <p class="tpick__hint" :class="{ 'tpick__hint--warn': hintWarn }" role="status">{{ hintText }}</p>
  </div>
</template>

<script>
// Fahrt wählen im Dialog „Ticket umbuchen“ (Kundenwunsch 13.10.2026: „beim umbuchen von shop fahrten ist das modal
// glaube ich noch komisch“; ersetzt ShopTripDataGrid mit Filterzeile, DevExtreme-Seitenleiste und 5er-Seiten): oben
// Monat (vorbelegt mit dem Monat der Fahrt des Tickets) und Suche, darunter die Fahrten des Monats wie auf der Seite
// Fahrten – Uhrzeit, Datum, Linie, Fahrt, Strecke, Status, freie Plätze mit Auslastungsbalken (grün, ab 50 % gelb,
// ab 80 % rot) –, sortiert nach Datum und Uhrzeit, 360 hoch mit virtuellem Scrollen; die aktuelle Fahrt des Tickets
// trägt „aktuell“ und steht beim Öffnen im Blick. Stornierte Fahrten und die aktuelle Fahrt lassen sich nicht wählen
// (Hinweis darunter). Daten wie die Seite Fahrten (GET shoptrip?month&year). Ereignis select (Fahrt bzw. null).
import { DxDataGrid } from "devextreme-vue/data-grid";
import DataGridMixin from "@/mixins/DataGridMixin";
import CustomStore from "devextreme/data/custom_store";
import axios from "axios";
import moment from "moment";
import { formatNumber } from "@/utils/format";

export default {
  name: "ShopTripPicker",
  components: { DxDataGrid },
  mixins: [DataGridMixin],
  props: {
    // umzubuchendes Ticket (trip_id, trip_day)
    ticket: { type: Object, default: null },
  },
  data() {
    return {
      gridRefName: "shopTripPicker",
      month: this.monthOf(this.ticket),
      searchText: "",
      selected: null,
      blocked: null,
      gridAttr: { class: "app-grid--detail" },
      pickScrolling: { mode: "virtual", rowRenderingMode: "virtual", showScrollbar: "onHover" },
      pickPaging: { enabled: false },
      columns: [
        { dataField: "start", caption: "Uhrzeit", width: 88, sortOrder: "asc", sortIndex: 1, cellTemplate: "timeTemplate" },
        {
          dataField: "day",
          caption: "Datum",
          width: 104,
          dataType: "date",
          sortOrder: "asc",
          sortIndex: 0,
          calculateDisplayValue: (row) => (row.day ? row.day_formatted : ""),
          cellTemplate: "dayTemplate",
        },
        { dataField: "line_id", caption: "Linie", width: 64, alignment: "left" },
        { dataField: "trip_id", caption: "Fahrt", width: 112, alignment: "left", cellTemplate: "tripTemplate" },
        { dataField: "description", caption: "Strecke", minWidth: 160, cellTemplate: "routeTemplate" },
        { dataField: "state", caption: "Status", width: 112, cellTemplate: "statusTemplate" },
        { name: "seats", caption: "Plätze", width: 168, cellTemplate: "seatsTemplate", allowSorting: false },
      ],
      tripDataSource: new CustomStore({
        key: ["trip_id", "version_id"],
        load: async () => {
          const m = moment(this.month + "-01");
          const data = (await axios.get(`${this.$store.state.baseUrl}shoptrip?month=${m.format("MM")}&year=${m.format("YYYY")}`)).data;
          return Array.isArray(data) ? data : [];
        },
      }),
    };
  },
  computed: {
    noDataText() {
      return "Keine Fahrten in diesem Monat";
    },
    hintWarn() {
      return !!this.blocked;
    },
    hintText() {
      if (this.blocked === "canceled") {
        return "Diese Fahrt ist storniert und kann nicht gewählt werden.";
      }
      if (this.blocked === "current") {
        return "Das Ticket gilt bereits für diese Fahrt.";
      }
      if (this.selected) {
        return `Gewählt: Fahrt ${this.selected.trip_id}, ${this.dayText(this.selected)} ${this.selected.start || ""} · ${this.routeText(this.selected)}`;
      }
      return "Neue Fahrt in der Liste wählen.";
    },
  },
  watch: {
    month() {
      this.clearSelection();
      const grid = this.grid();
      if (grid) {
        grid.refresh();
      }
    },
  },
  methods: {
    grid() {
      const ref = this.$refs[this.gridRefName];
      return ref && ref.instance;
    },
    monthOf(ticket) {
      const m = ticket && ticket.trip_day ? moment(ticket.trip_day) : null;
      return m && m.isValid() ? m.format("YYYY-MM") : moment().format("YYYY-MM");
    },
    // beim erneuten Öffnen für ein anderes Ticket: Monat, Suche und Auswahl zurücksetzen
    reset() {
      this.searchText = "";
      this.clearSelection();
      const month = this.monthOf(this.ticket);
      if (this.month !== month) {
        this.month = month;
      } else {
        const grid = this.grid();
        if (grid) {
          grid.searchByText("");
          grid.refresh();
        }
      }
    },
    clearSelection() {
      this.selected = null;
      this.blocked = null;
      const grid = this.grid();
      if (grid && grid.option("focusedRowIndex") !== -1) {
        grid.option("focusedRowIndex", -1);
      }
      this.$emit("select", null);
    },
    isCurrent(row) {
      return !!this.ticket && !!row && String(row.trip_id) === String(this.ticket.trip_id) && this.sameDay(row);
    },
    sameDay(row) {
      if (!this.ticket || !this.ticket.trip_day || !row.day) {
        return true;
      }
      return moment(row.day).format("YYYY-MM-DD") === moment(this.ticket.trip_day).format("YYYY-MM-DD");
    },
    dayText(row) {
      const m = row && row.day ? moment(row.day).locale("de") : null;
      return m && m.isValid() ? m.format("dd, DD.MM.") : (row && row.day_formatted) || "";
    },
    routeText(row) {
      return String((row && row.description) || "–").replace(/\s*->\s*/g, " → ");
    },
    freeText(row) {
      const cap = Number(row.capacity) || 0;
      const free = Math.max(0, cap - (Number(row.capacity_used) || 0));
      return `${formatNumber(free)} frei`;
    },
    seatsColor(row) {
      const cap = Number(row.capacity) || 0;
      const pct = cap > 0 ? ((Number(row.capacity_used) || 0) * 100) / cap : 0;
      return pct >= 80 ? "st-late" : pct >= 50 ? "st-warn" : "st-ok";
    },
    onSearchInput() {
      const grid = this.grid();
      if (grid) {
        grid.searchByText(this.searchText || "");
      }
    },
    onToolbarPreparing(e) {
      e.toolbarOptions.items = [];
      e.toolbarOptions.visible = false;
    },
    // stornierte Fahrten blasser
    onRowPrepared(e) {
      if (e.rowType !== "data" || !e.data) {
        return;
      }
      const el = e.rowElement && e.rowElement.get ? e.rowElement.get(0) : e.rowElement;
      if (el && el.classList) {
        el.classList.toggle("tpick-row--off", e.data.state != "ACTIVE");
      }
    },
    // aktuelle Fahrt des Tickets nach dem Laden in den Blick holen (ohne sie zu wählen)
    onContentReady(e) {
      if (this.revealed || !this.ticket) {
        return;
      }
      this.revealed = true;
      const grid = e.component;
      const items = grid.getDataSource() ? grid.getDataSource().items() : [];
      const current = items.find((row) => this.isCurrent(row));
      if (current) {
        grid.navigateToRow(grid.keyOf(current));
      }
    },
    onFocusedRowChanging(e) {
      const row = e.rows && e.rows[e.newRowIndex];
      if (!row || !row.data) {
        return;
      }
      if (row.data.state != "ACTIVE" || this.isCurrent(row.data)) {
        e.cancel = true;
        this.blocked = row.data.state != "ACTIVE" ? "canceled" : "current";
      }
    },
    onFocusedRowChanged(e) {
      const row = e.row;
      if (!row || row.rowType !== "data" || !row.data) {
        return;
      }
      this.blocked = null;
      this.selected = Object.freeze(Object.assign({}, row.data));
      this.$emit("select", this.selected);
    },
  },
  created() {
    this.revealed = false;
  },
};
</script>

<style scoped>
.tpick {
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-width: 0;
}
.tpick__bar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
}
.tpick__search {
  flex: 1 1 220px;
  min-width: 0;
}
.tpick__grid {
  border: 1px solid var(--line);
  border-radius: var(--r-ctl);
  overflow: hidden;
}
.tpick__grid ::v-deep .dx-sort-index-icon {
  display: none;
}
.tpick__grid ::v-deep .tpick__time {
  font-weight: 600;
  color: var(--ink);
  font-variant-numeric: tabular-nums;
}
.tpick__grid ::v-deep .tpick__num {
  font-variant-numeric: tabular-nums;
}
.tpick__grid ::v-deep .tpick__route {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.tpick__grid ::v-deep .tpick__trip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.tpick__grid ::v-deep .tpick__current {
  padding: 0 6px;
  border-radius: var(--r-ctl);
  background: var(--sel);
  color: var(--link-tint);
  font-size: 11px;
  line-height: 18px;
  font-weight: 600;
}
.tpick__grid ::v-deep .tpick__status {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  white-space: nowrap;
}
.tpick__grid ::v-deep .tpick__dot {
  flex: none;
  width: 7px;
  height: 7px;
  border-radius: 50%;
}
.tpick__grid ::v-deep .tpick__dot--ok {
  background: var(--green-dot);
}
.tpick__grid ::v-deep .tpick__dot--off {
  background: var(--red);
}
.tpick__grid ::v-deep .tpick__seats {
  display: flex;
  align-items: center;
  gap: 8px;
}
.tpick__grid ::v-deep .tpick__bar-seats {
  flex: 1 1 auto;
  min-width: 40px;
}
.tpick__grid ::v-deep .dx-data-row.tpick-row--off > td {
  color: var(--ink-3);
}
p.tpick__hint {
  margin: 0;
  font-size: 12px;
  line-height: 16px;
  color: var(--ink-3);
}
p.tpick__hint--warn {
  color: var(--amber);
}
</style>
